/**
 * Pool Import Schemas (BL-207, SR3)
 *
 * Contract for the Tonnie-internal full-profile Pool import: real `talents`
 * rows (name/contact/role/location/skills + pool opt-in) created on behalf of
 * screened candidates, distinct from the email-only `journey_invitations`
 * bulk-invite. Used by the business-web upload wizard and mirrored by the
 * backend controller (`POST /api/business/pool/import`).
 *
 * Error messages are i18n KEYS (per the cohort.ts convention) — the form
 * resolves them via `t()`; no hardcoded English.
 */
import { z } from 'zod';

/**
 * ISO-3166-1 alpha-3 country code — the IMPORT contract (a recruiter's CSV says
 * "PRT"). It is not the stored form: `PoolImportService` converts it to alpha-2
 * with `toAlpha2CountryCode` from `@tonnie/foundation` before it reaches
 * `talents.current_country`, which every reader treats as alpha-2 (F34).
 */
const countryAlpha3 = z
  .string()
  .trim()
  .length(3, 'business.poolImport.errors.country')
  .toUpperCase();

export const poolImportRowSchema = z.object({
  email: z.string().trim().toLowerCase().email('business.poolImport.errors.email'),
  firstName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  jobTitle: z.string().trim().max(200).optional(),
  currentCountry: countryAlpha3.optional(),
  currentCity: z.string().trim().max(100).optional(),
  skills: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
});

export const poolImportSchema = z.object({
  talents: z
    .array(poolImportRowSchema)
    .min(1, 'business.poolImport.errors.empty')
    .max(1000, 'business.poolImport.errors.tooMany'),
});

export type PoolImportRow = z.infer<typeof poolImportRowSchema>;
export type PoolImportInput = z.infer<typeof poolImportSchema>;

export interface PoolImportResult {
  created: number;
  updated: number;
  skipped: number;
  total: number;
}

/**
 * Single-candidate full ingest (Pool smart search, BL-207).
 *
 * Extends a pool row with the free-text professional `summary` extracted from
 * the candidate's CV. The ingest builds `pool_source_text` from these fields,
 * embeds it (brain), stores the CV in Vault, and links `cv_vault_file_id`.
 * Used by the one-time roster ops script and (later) the pool ingest UI.
 */
export const poolCandidateIngestSchema = poolImportRowSchema.extend({
  /** One-paragraph professional summary parsed from the CV — feeds the embedding. */
  summary: z.string().trim().max(4000).optional(),
});

export type PoolCandidateIngest = z.infer<typeof poolCandidateIngestSchema>;

export interface PoolCandidateIngestResult {
  talentId: string;
  userId: string;
  /** true when the candidate's user shell was created (vs. an idempotent refresh). */
  created: boolean;
  /** true when a semantic embedding was written to `profile_embedding`. */
  embedded: boolean;
  /** true when a CV file was stored in Vault and linked via `cv_vault_file_id`. */
  cvStored: boolean;
}
